import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, combineLatest, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { AccountFacade } from 'ish-core/facades/account.facade';
import { GoogleTagManagerFacade } from 'ish-core/facades/gtm.facade';
import { UserService } from 'ish-core/services/user/user.service';
import { deleteBasketItems } from 'ish-core/store/customer/basket';
import { loginUserSuccess } from 'ish-core/store/customer/user';

import {
  PRESIDIO_MOCK_CSR_DISPLAY_NAME,
  PRESIDIO_MOCK_CSR_LOGIN,
  getPresidioMockActiveLogin,
  isPresidioMockCsrLogin,
  setPresidioMockActiveLogin,
} from '../mock/presidio-mock-session';
import {
  PresidioImpersonationCandidate,
  PresidioImpersonationService,
  PresidioImpersonationState,
} from './presidio-impersonation.service';

export type { PresidioImpersonationCandidate, PresidioImpersonationState };

/**
 * CUSTOMIZATION: MVP 8.2.2 — CSR impersonation facade (checkout blocked while active; ATC allowed).
 */
@Injectable({ providedIn: 'root' })
export class PresidioImpersonationFacade {
  private service = inject(PresidioImpersonationService);
  private userService = inject(UserService);
  private accountFacade = inject(AccountFacade);
  private store = inject(Store);
  private gtm = inject(GoogleTagManagerFacade);

  private readonly refresh$ = new BehaviorSubject<void>(undefined);

  /** CSR when mock session says so, or when the signed-in user is Quinn. */
  readonly canImpersonate$: Observable<boolean> = combineLatest([
    this.accountFacade.user$.pipe(map(u => (u?.login || '').trim().toLowerCase())),
    this.refresh$,
  ]).pipe(
    tap(([login]) => {
      if (login === PRESIDIO_MOCK_CSR_LOGIN.toLowerCase() && !isPresidioMockCsrLogin()) {
        setPresidioMockActiveLogin(PRESIDIO_MOCK_CSR_LOGIN);
      }
    }),
    map(([login]) => login === PRESIDIO_MOCK_CSR_LOGIN.toLowerCase() || isPresidioMockCsrLogin()),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly state$: Observable<PresidioImpersonationState> = this.refresh$.pipe(
    map(() => this.service.getState()),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly isImpersonating$: Observable<boolean> = this.state$.pipe(
    map(s => s.impersonating),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly impersonatedDisplayName$: Observable<string> = this.state$.pipe(
    map(s => s.displayName),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * CSR operator name for header chrome (next to cart).
   * Stays Quinn while co-browsing — Viewing as owns the end-user name.
   */
  readonly actorDisplayName$: Observable<string> = this.refresh$.pipe(
    map(() => (isPresidioMockCsrLogin() ? PRESIDIO_MOCK_CSR_DISPLAY_NAME : '')),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly candidates$: Observable<PresidioImpersonationCandidate[]> = this.refresh$.pipe(
    switchMap(() => this.service.searchCandidates('')),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  searchCandidates(query: string, limit = 50): Observable<PresidioImpersonationCandidate[]> {
    return this.service.searchCandidates(query, limit);
  }

  /** Call after CSR login so canImpersonate$ / candidates update. */
  refresh(): void {
    this.refresh$.next();
  }

  async start(login: string): Promise<boolean> {
    // Ensure mock session matches Quinn even after token restore / refresh
    if (!isPresidioMockCsrLogin()) {
      setPresidioMockActiveLogin(PRESIDIO_MOCK_CSR_LOGIN);
    }
    if (!isPresidioMockCsrLogin()) {
      return false;
    }
    const state = await firstValueFrom(this.service.start(login));
    if (!state.impersonating) {
      this.refresh();
      return false;
    }
    this.store.dispatch(deleteBasketItems());
    await this.reloadCustomerSession();
    this.refresh();
    this.gtm.triggeringGTMEvent('impersonation_start', { location: 'header' });
    return true;
  }

  async end(): Promise<void> {
    await firstValueFrom(this.service.end());
    this.store.dispatch(deleteBasketItems());
    await this.reloadCustomerSession();
    this.refresh();
    this.gtm.triggeringGTMEvent('impersonation_end', { location: 'banner' });
  }

  private async reloadCustomerSession(): Promise<void> {
    try {
      const customerLogin = await firstValueFrom(this.userService.signInUserByToken());
      if (customerLogin) {
        this.store.dispatch(loginUserSuccess(customerLogin));
      }
    } catch {
      // Keep current store; REST rewrite still applies on subsequent calls.
    }
  }

  /** Sync check for click handlers (session is LOCAL_MOCK source of truth). */
  isImpersonatingNow(): boolean {
    return this.service.isImpersonating();
  }

  /** True when current mock login is the CSR actor (for templates that need sync check). */
  isCsrActor(): boolean {
    return isPresidioMockCsrLogin(getPresidioMockActiveLogin());
  }
}
